import React, { useEffect, useRef, useState } from "react";
import {
  useEnsureDsaSignatureMutation,
  useGetDsaIssuerQuery,
  useRegenerateDsaSignatureMutation,
  useRemoveDsaIssuerImageMutation,
  useUploadDsaIssuerImageMutation,
} from "src/services/DsaIssuerService";
import {
  DEFAULT_SIGNATURE_INK,
  DEFAULT_SIGNATURE_STYLE,
  generateSignature,
  SIGNATURE_INK_OPTIONS,
  SIGNATURE_STYLE_OPTIONS,
  SignatureInk,
  SignatureStyle,
} from "src/pages/employer-certificate/generateSignature";
import { showToast } from "src/utils/toaster/showToast";

/**
 * "Employer Certificate Signature (DSA)" on a retailer's / distributor's
 * profile: the signature their DSA (175) certificates print.
 *
 * It is generated in handwriting from the profile name the first time this
 * card is opened (or with the first certificate, whichever comes first) and
 * reused after that; here the admin can regenerate it or upload a real one.
 *
 * The firm name and seal are not per agent: each DSA application prints those
 * of the Employer Certificate Logo entry it was given.
 */

type Props = { userId?: string };

// Checkerboard, so a transparent PNG is visibly transparent.
const checkerboard: React.CSSProperties = {
  backgroundImage:
    "linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#eee 75%),linear-gradient(-45deg,transparent 75%,#eee 75%)",
  backgroundSize: "16px 16px",
  backgroundPosition: "0 0,0 8px,8px -8px,-8px 0",
};

const outlineButton =
  "cursor-pointer text-center text-sm border border-primary-main text-primary-main rounded py-1.5 px-3 hover:bg-slate-50 disabled:opacity-50";

const report = (res: any, fallback: string) => {
  if (res?.error || !res?.data?.status) {
    showToast("error", res?.error?.data?.message || res?.data?.message || fallback);
    return false;
  }
  showToast("success", res?.data?.message);
  return true;
};

const DsaIssuerCard = ({ userId }: Props) => {
  const { data, isFetching, isLoading } = useGetDsaIssuerQuery(userId || "", {
    skip: !userId,
  });
  const issuer = data?.data;
  const [uploadSignature, signatureUploadInfo] = useUploadDsaIssuerImageMutation();
  const [removeImage, removeInfo] = useRemoveDsaIssuerImageMutation();
  const [regenerate, regenerateInfo] = useRegenerateDsaSignatureMutation();
  const [ensureSignature] = useEnsureDsaSignatureMutation();
  const [isGenerating, setIsGenerating] = useState(false);
  // Handwriting style and pen ink for "Generate" (default: Handwritten look, black pen); each click draws a new one.
  const [style, setStyle] = useState<SignatureStyle>(DEFAULT_SIGNATURE_STYLE);
  const [ink, setInk] = useState<SignatureInk>(DEFAULT_SIGNATURE_INK);
  const autoTriedFor = useRef<string>("");

  /*
    No signature yet: make it now from the profile name, once per agent per
    visit. Save-once on the API side (POST /dsa-issuer/ensure-signature), so a
    certificate being generated at the same moment cannot end up with a
    different one. Quiet on failure — the first certificate tries again.
  */
  useEffect(() => {
    if (!userId || isLoading || isFetching || !issuer) return;
    if (issuer.signatureImage || !issuer.profileName) return;
    if (autoTriedFor.current === userId) return;
    autoTriedFor.current = userId;
    setIsGenerating(true);
    generateSignature(issuer.profileName)
      .then(({ image, name }) => ensureSignature({ userId, image, name }))
      .catch(() => undefined)
      .finally(() => setIsGenerating(false));
  }, [userId, isLoading, isFetching, issuer, ensureSignature]);

  if (!userId) return null;

  const upload = (file?: File | null) => {
    if (!file) return;
    const body = new FormData();
    body.append("image", file);
    uploadSignature({ userId, kind: "signature", body }).then((res: any) =>
      report(res, "Unable to upload.")
    );
  };

  const remove = () => {
    if (
      !window.confirm(
        "Remove the signature? A new one is generated from the profile name the next time it is needed."
      )
    )
      return;
    removeImage({ userId, kind: "signature" }).then((res: any) =>
      report(res, "Unable to remove.")
    );
  };

  const handleRegenerate = async () => {
    if (
      issuer?.signatureImage &&
      !window.confirm(
        "Replace the current signature? Certificates generated from now on will print the new one."
      )
    )
      return;
    setIsGenerating(true);
    try {
      const { image, name } = await generateSignature(issuer?.profileName, {
        style,
        ink,
      });
      const res: any = await regenerate({ userId, image, name });
      report(res, "Unable to save the signature.");
    } catch (err: any) {
      showToast("error", err?.message || "Unable to generate the signature.");
    } finally {
      setIsGenerating(false);
    }
  };

  const signatureLabel =
    issuer?.signatureSource === "GENERATED"
      ? `Generated from "${issuer?.signatureName}"${issuer?.signatureUpdatedAt ? ` on ${issuer.signatureUpdatedAt.slice(0, 10)}` : ""}`
      : issuer?.signatureSource === "UPLOADED"
      ? `Uploaded${issuer?.signatureUpdatedAt ? ` on ${issuer.signatureUpdatedAt.slice(0, 10)}` : ""}`
      : isGenerating
      ? "Generating from the profile name..."
      : "Not created yet. It is generated from the profile name.";

  const busy = isGenerating || regenerateInfo.isLoading;

  return (
    <div className="bg-white rounded-lg shadow border border-slate-200 p-4 m-3 flex flex-col gap-4">
      <div className="font-semibold text-primary-main text-base border-b pb-2">
        Employer Certificate Signature (DSA)
        {isFetching && <span className="ml-2 text-xs text-slate-500 font-normal">Loading...</span>}
      </div>

      <div className="flex gap-6 flex-wrap items-start">
        <div className="w-[260px] flex flex-col gap-2">
          <div
            className="h-[140px] border border-dashed border-slate-400 rounded flex items-center justify-center overflow-hidden"
            style={checkerboard}
          >
            {issuer?.signatureImage ? (
              <img
                src={issuer.signatureImage}
                alt="Signature"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-xs text-slate-500 px-3 text-center">
                {busy ? "Generating..." : "No signature yet"}
              </span>
            )}
          </div>
          <span className="text-[11px] text-slate-600 leading-snug">{signatureLabel}</span>
          <div className="flex gap-2">
            <select
              className="flex-1 min-w-0 text-xs border border-slate-300 rounded px-1 py-1"
              value={style}
              onChange={(e) => setStyle(e.target.value as SignatureStyle)}
              disabled={busy}
              title="Handwriting style"
            >
              {SIGNATURE_STYLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              className="text-xs border border-slate-300 rounded px-1 py-1"
              value={ink}
              onChange={(e) => setInk(e.target.value as SignatureInk)}
              disabled={busy}
              title="Pen ink"
            >
              {SIGNATURE_INK_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            className={outlineButton}
            onClick={handleRegenerate}
            disabled={busy || !issuer?.profileName}
          >
            {busy
              ? "Generating..."
              : issuer?.signatureImage
              ? "Regenerate from Profile Name"
              : "Generate from Profile Name"}
          </button>
          <label className={outlineButton}>
            {signatureUploadInfo.isLoading ? "Uploading..." : "Upload Signature"}
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              disabled={signatureUploadInfo.isLoading}
              onChange={(e) => {
                upload(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
          </label>
          {issuer?.signatureImage && (
            <button
              type="button"
              className="text-xs text-red-600 underline"
              onClick={remove}
              disabled={removeInfo.isLoading}
            >
              Remove signature
            </button>
          )}
        </div>

        <div className="flex-1 min-w-[240px] text-[12px] text-slate-600 leading-relaxed">
          This handwritten signature, made from the profile name, prints on this
          agent's DSA (175) Employer Certificates.
          <br />
          The firm name is this profile's firm name. The seal comes from the{" "}
          <b>Employer Certificate Logo</b> entry each application is given.
        </div>
      </div>
    </div>
  );
};

export default DsaIssuerCard;
